// useToaster.js
import { useEffect, useMemo, useRef } from 'react';
import { toaster, KIND } from 'baseui/toast';

// Pretty-print AWS/GraphQL-like errors (safe on plain strings too)
function formatErrorMessage(error) {
  try {
    const parsed = typeof error === 'string' ? JSON.parse(error) : error;
    const msg =
      parsed?.response?.errors?.[0]?.message ??
      parsed?.message ??
      String(error);
    const reqId = parsed?.response?.headers?.map?.['x-amzn-requestid'];
    const op = parsed?.request?.query?.split('\n')?.[1]?.trim();
    const lines = [
      op ? `Operation: ${op}` : null,
      `Error: ${msg}`,
      reqId ? `Request ID: ${reqId}` : null,
    ].filter(Boolean);
    return lines.join('\n');
  } catch {
    return typeof error === 'string' ? error : String(error);
  }
}

/**
 * Show a toast when `message` changes.
 * Usage: useToaster(successMessage, 'success');
 *
 * @param {string|React.ReactNode} message
 * @param {'success'|'error'|'warning'|'info'} type
 * @param {object} options - Base Web toast options (autoHideDuration, overrides, etc.)
 * @param {{ dedupeMs?: number, formatErrors?: boolean }} runtime
 */
export default function useToaster(
  message,
  type = 'error',
  options = {},
  { dedupeMs = 8000, formatErrors = true } = {}
) {
  const lastErrorRef = useRef({ text: null, ts: 0 });

  // Avoid re-triggering the effect just because a new options object was created
  const stableOptions = useMemo(() => options, [
    // only stringify shallowly relevant fields; remove this if you pass a stable object yourself
    options.autoHideDuration,
    JSON.stringify(options.overrides ?? null),
    options?.closeable,
  ]);

  useEffect(() => {
    const isEmpty =
      message == null ||
      (typeof message === 'string' && message.trim() === '');
    if (isEmpty) return;

    const typeMap = {
      success: KIND.positive,
      error: KIND.negative,
      warning: KIND.warning,
      info: KIND.info,
    };
    const kind = typeMap[type] ?? KIND.negative;

    // Default durations by kind (can be overridden)
    const defaultDurations = {
      [KIND.positive]: 5000,
      [KIND.negative]: 8000,
      [KIND.warning]: 6000,
      [KIND.info]: 4000,
    };

    // format only errors (preserve newlines with a body override)
    const finalMessage =
      kind === KIND.negative && formatErrors ? formatErrorMessage(message) : message;

    const merged = {
      autoHideDuration: defaultDurations[kind],
      ...stableOptions,
      // ensure multi-line errors wrap nicely unless you already override it
      overrides: {
        ToastBody: {
          style: ({ $theme }) => ({
            whiteSpace: 'pre-wrap',
            ...((stableOptions.overrides &&
              stableOptions.overrides.ToastBody &&
              stableOptions.overrides.ToastBody.style) ||
              {}),
          }),
        },
        ...(stableOptions.overrides || {}),
      },
    };

    // Deduplicate identical errors within a time window
    if (kind === KIND.negative && dedupeMs > 0) {
      const now = Date.now();
      const textKey = typeof message === 'string' ? message : JSON.stringify(message);
      if (
        lastErrorRef.current.text === textKey &&
        now - lastErrorRef.current.ts < dedupeMs
      ) {
        return;
      }
      lastErrorRef.current = { text: textKey, ts: now };
    }

    // Use unified toaster.show() method with KIND
    toaster.show(finalMessage, {
      ...merged,
      kind: kind
    });
  }, [message, type, stableOptions, dedupeMs, formatErrors]);
}
